import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ProfileFormValues } from "./profileFormTypes";
import { Building2, MapPin, User } from "lucide-react";
import { Input } from "@/components/ui";
import { AddressFields } from "./AddressFields";
import { DocumentFields } from "./DocumentFields";
import { useTaxIdentifierHint } from "./useTaxIdentifierHint";
import type { ProfileSectionControls } from "./profileSectionTypes";
import { ProfileSectionCard } from "./ProfileSectionCard";

interface CompanyFormSectionProps {
  register: UseFormRegister<ProfileFormValues>;
  setValue: UseFormSetValue<ProfileFormValues>;
  watch: UseFormWatch<ProfileFormValues>;
  errors: FieldErrors<ProfileFormValues>;
  profileSyncKey: string;
  registeredAddressSyncKey: string;
  sections: {
    business: ProfileSectionControls;
    registered: ProfileSectionControls;
    legalRep: ProfileSectionControls;
  };
}

export function CompanyFormSection({
  register,
  setValue,
  watch,
  errors,
  registeredAddressSyncKey,
  sections,
}: CompanyFormSectionProps) {
  const { t } = useTranslation("settings");
  const vatNumber = watch("vat_number");
  const fiscalCode = watch("fiscal_code");
  const vatLookup = useTaxIdentifierHint(vatNumber, "vat");
  const fiscalLookup = useTaxIdentifierHint(fiscalCode, "fiscal_code");

  return (
    <>
      <ProfileSectionCard
        title={t("forms.company.cardBusiness")}
        icon={Building2}
        {...sections.business}
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input
              {...register("company_name")}
              label={t("forms.company.companyName")}
              required
              placeholder={t("forms.company.companyNamePh")}
              error={errors.company_name?.message}
            />
          </div>
          <Input
            {...register("vat_number")}
            label={t("forms.company.vatNumber")}
            required
            placeholder={t("forms.company.vatNumberPh")}
            error={errors.vat_number?.message}
            hint={vatLookup.hint}
            rightIcon={vatLookup.rightIcon}
          />
          <Input
            {...register("fiscal_code")}
            label={t("forms.company.taxId")}
            placeholder={t("forms.company.taxIdPh")}
            error={errors.fiscal_code?.message}
            hint={fiscalLookup.hint}
            rightIcon={fiscalLookup.rightIcon}
            required
            className="uppercase"
          />
        </div>
      </ProfileSectionCard>

      <ProfileSectionCard
        title={t("forms.company.cardRegistered")}
        icon={MapPin}
        {...sections.registered}
      >
        <AddressFields
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          syncKey={registeredAddressSyncKey}
        />
      </ProfileSectionCard>

      <ProfileSectionCard
        title={t("forms.company.cardLegalRep")}
        icon={User}
        {...sections.legalRep}
      >
        <div className="space-y-6">
          <Input
            {...register("legal_rep_name")}
            label={t("forms.company.fullName")}
            required
            placeholder={t("forms.company.fullNamePh")}
            error={errors.legal_rep_name?.message}
          />
          <DocumentFields register={register} errors={errors} />
        </div>
      </ProfileSectionCard>
    </>
  );
}
